import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import {
  formatFinancialYearLabel,
  getCalendarMonthFromFySlot,
  getFinancialYearDropdownOptions,
  getFinancialYearMonthSequence,
} from "../utils/financialYear";
import {
  monthlySaleReportQuery,
  readMonthlySaleReportParams,
} from "../utils/monthlySaleReportNav";
import { useSearchParams } from "react-router-dom";
import {
  buildByBrandSaleReturn,
  clearPrecalcMemoryCache,
  generateAndSaveMonthlyImportPrecalc,
  loadMonthlyImportPrecalc,
} from "../services/monthlyImportPrecalcService";
import { MONTHLY_AUTOS_BRANDS, clearImportMonthlyReportCache } from "../services/monthlyAutosReportService";
import {
  emptyTopSalesValues,
  resolveMonthlyTopSales,
  saveMonthlyTopSales,
  applyTopSalesToReport,
} from "../services/monthlyTopSalesService";
import {
  applySaleCollectionToReport,
  clearMonthlySaleCollection,
  loadMonthlySaleCollection,
  saveMonthlySaleCollection,
} from "../services/monthlySaleCollectionService";
import "./MonthlySaleReportPage.css";

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const BRANCH_OPTIONS = [
  { value: "Main", label: "Main Branch" },
  { value: "Branch 2", label: "Spareway" },
  { value: "Branch 3", label: "Branch" },
  { value: "Wholesale", label: "Wholesale" },
];

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function fmtPct(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toFixed(2)}%`;
}

function formatGeneratedAt(v) {
  if (v == null) return null;
  if (typeof v?.toDate === "function") return v.toDate().toLocaleString();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

function sumBreakdownRows(rows) {
  return (rows || []).reduce(
    (acc, r) => ({
      sale: acc.sale + (Number(r.sale) || 0),
      purchase: acc.purchase + (Number(r.purchase) || 0),
      transferOut: acc.transferOut + (Number(r.transferOut) || 0),
      transferIn: acc.transferIn + (Number(r.transferIn) || 0),
      saleReturn: acc.saleReturn + (Number(r.saleReturn) || 0),
    }),
    { sale: 0, purchase: 0, transferOut: 0, transferIn: 0, saleReturn: 0 }
  );
}

function SaleReturnBrandTable({ rows, total }) {
  if (!rows?.length) {
    return (
      <section className="msr-precalc-panel">
        <h2 className="msr-precalc-title">Sale Return — brand-wise</h2>
        <p className="msr-precalc-empty">No sale return (credit note) for this month.</p>
      </section>
    );
  }

  return (
    <section className="msr-precalc-panel">
      <h2 className="msr-precalc-title">Sale Return — brand-wise</h2>
      <p className="msr-precalc-meta">
        From import Credit Note vouchers · line Amount by BrandName · voucher total capped by BillAmount
      </p>
      <div className="msr-precalc-table-wrap">
        <table className="msr-precalc-table">
          <thead>
            <tr>
              <th>Brand</th>
              <th className="msr-num">Sale Return</th>
              <th className="msr-num">OE</th>
              <th className="msr-num">Ordinary</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td className="msr-num">{fmt(row.saleReturn)}</td>
                <td className="msr-num">{fmt(row.oe)}</td>
                <td className="msr-num">{fmt(row.ordinary)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="font-semibold">Total</td>
              <td className="msr-num font-semibold">{fmt(total)}</td>
              <td className="msr-num font-semibold">{fmt(rows.reduce((s, r) => s + r.oe, 0))}</td>
              <td className="msr-num font-semibold">{fmt(rows.reduce((s, r) => s + r.ordinary, 0))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function BreakdownTable({ title, rows, meta, printLabel, onPrint, printClassName }) {
  const totals = useMemo(() => sumBreakdownRows(rows), [rows]);

  if (!rows?.length) {
    return (
      <section className={`msr-precalc-panel ${printClassName || ""}`}>
        <div className="msr-precalc-title-row">
          <h2 className="msr-precalc-title">{title}</h2>
        </div>
        <p className="msr-precalc-empty">No rows for this month.</p>
      </section>
    );
  }

  return (
    <section className={`msr-precalc-panel ${printClassName || ""}`}>
      <div className="msr-precalc-title-row">
        <h2 className="msr-precalc-title">{title}</h2>
        {onPrint ? (
          <button type="button" className="msr-group-print-btn msr-no-print" onClick={onPrint}>
            Print
          </button>
        ) : null}
      </div>
      {printLabel ? <p className="msr-group-print-period">{printLabel}</p> : null}
      {meta ? <p className="msr-precalc-meta">{meta}</p> : null}
      <div className="msr-precalc-table-wrap">
        <table className="msr-precalc-table">
          <thead>
            <tr>
              <th>Name</th>
              <th className="msr-num">Sale</th>
              <th className="msr-num">Purchase</th>
              <th className="msr-num">Transfer Out</th>
              <th className="msr-num">Transfer In</th>
              <th className="msr-num">Sale Return</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td className="msr-num">{fmt(row.sale)}</td>
                <td className="msr-num">{fmt(row.purchase)}</td>
                <td className="msr-num">{fmt(row.transferOut)}</td>
                <td className="msr-num">{fmt(row.transferIn)}</td>
                <td className="msr-num">{fmt(row.saleReturn)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="font-semibold">Total ({rows.length} rows)</td>
              <td className="msr-num font-semibold">{fmt(totals.sale)}</td>
              <td className="msr-num font-semibold">{fmt(totals.purchase)}</td>
              <td className="msr-num font-semibold">{fmt(totals.transferOut)}</td>
              <td className="msr-num font-semibold">{fmt(totals.transferIn)}</td>
              <td className="msr-num font-semibold">{fmt(totals.saleReturn)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function formatSourceMonth(year, monthIndex) {
  if (year == null || monthIndex == null) return "";
  return `${MONTH_OPTIONS[monthIndex]} ${year}`;
}

function TopSaleEditorPopup({
  open,
  onClose,
  year,
  monthIndex,
  branchLabel,
  resolved,
  values,
  onChange,
  onSave,
  saving,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const sourceLabel = formatSourceMonth(resolved?.sourceYear, resolved?.sourceMonthIndex);
  let note =
    "No Top Sale saved yet. Enter amounts and save — later months keep these until you save a new month.";
  if (resolved?.found && resolved.carried && sourceLabel) {
    note = `Showing ${sourceLabel} values. They stay until you save this month (or a later month).`;
  } else if (resolved?.found && !resolved.carried) {
    note = `Saved for ${MONTH_OPTIONS[monthIndex]} ${year}. Later months use these until you enter a new month.`;
  } else if (resolved?.legacy) {
    note =
      "Loaded from older manual Top Sale entries. Save here to store them in the Top Sale collection.";
  }

  return (
    <div
      className="msr-manual-modal-backdrop"
      role="presentation"
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div
        className="msr-manual-modal msr-top-sale-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Edit Top Sale"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="msr-manual-modal-head">
          <h3 className="msr-manual-overrides-title">
            Edit Top Sale — {MONTH_OPTIONS[monthIndex]} {year}
            {branchLabel ? ` · ${branchLabel}` : ""}
          </h3>
          <button type="button" className="msr-manual-delete" disabled={saving} onClick={onClose}>
            Close
          </button>
        </div>
        <p className="msr-precalc-meta">{note}</p>
        <div className="msr-precalc-table-wrap">
          <table className="msr-precalc-table">
            <thead>
              <tr>
                <th>Brand</th>
                <th className="msr-num">Top Sale</th>
              </tr>
            </thead>
            <tbody>
              {MONTHLY_AUTOS_BRANDS.map((brand) => (
                <tr key={brand}>
                  <td>{brand}</td>
                  <td className="msr-num">
                    <input
                      className="msr-top-sale-input"
                      type="text"
                      inputMode="decimal"
                      value={values[brand] ?? ""}
                      onChange={(e) => onChange(brand, e.target.value)}
                      placeholder="0"
                      aria-label={`${brand} top sale`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="msr-top-sale-actions">
          <button type="button" className="msr-print-btn" disabled={saving} onClick={onSave}>
            {saving ? "Saving…" : "Save Top Sale"}
          </button>
          <button type="button" className="msr-manual-delete" disabled={saving} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function SaleCollectionEditorPopup({
  open,
  onClose,
  year,
  monthIndex,
  branchLabel,
  value,
  onChange,
  onSave,
  onClear,
  saving,
  hasSaved,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  return (
    <div
      className="msr-manual-modal-backdrop"
      role="presentation"
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div
        className="msr-manual-modal msr-top-sale-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Edit Sale Collection"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="msr-manual-modal-head">
          <h3 className="msr-manual-overrides-title">
            Edit Sale Collection — {MONTH_OPTIONS[monthIndex]} {year}
            {branchLabel ? ` · ${branchLabel}` : ""}
          </h3>
          <button type="button" className="msr-manual-delete" disabled={saving} onClick={onClose}>
            Close
          </button>
        </div>
        <p className="msr-precalc-meta">
          {hasSaved
            ? "This month uses the saved amount on Monthly sale report (import). Saving again updates it."
            : "Enter the amount for this month. The import report SALE COLLECTION line will use it."}
        </p>
        <label className="msr-side-field" style={{ padding: "0 2px 8px" }}>
          <span>Sale Collection</span>
          <input
            className="msr-top-sale-input"
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="0"
            aria-label="Sale collection amount"
            autoFocus
          />
        </label>
        <div className="msr-top-sale-actions">
          <button type="button" className="msr-print-btn" disabled={saving} onClick={onSave}>
            {saving ? "Saving…" : "Save Sale Collection"}
          </button>
          {hasSaved ? (
            <button type="button" className="msr-manual-delete" disabled={saving} onClick={onClear}>
              Clear (use import)
            </button>
          ) : null}
          <button type="button" className="msr-manual-delete" disabled={saving} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportCollectionPanel({ report }) {
  if (!report) return null;
  const t = report.totals || {};
  const c = report.collection || {};
  const f = report.footer || {};
  const ss = report.saleSplit || {};
  const wd = report.workingDays || {};
  const avg = report.avgDaySale || {};

  const subtotal = (Number(t.cashSale) || 0) + (Number(c.saleCollection) || 0) + (Number(t.otherSale) || 0);
  const totalCashInHand =
    (Number(t.cashSale) || 0) +
    (Number(c.saleCollection) || 0) -
    (Number(c.lessCashDiscount) || 0) -
    (Number(c.lessSaleReturn) || 0);

  return (
    <section className="msr-precalc-panel">
      <h2 className="msr-precalc-title">Import report — collection &amp; totals</h2>
      <p className="msr-precalc-meta">
        Saved snapshot from monthly import report · same numbers as{" "}
        <strong>Monthly sale report (import)</strong>
      </p>
      <div className="msr-precalc-totals-grid">
        <div className="msr-precalc-total-box">
          <span>Cash Sale</span>
          <strong>{fmt(t.cashSale)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Credit Bill</span>
          <strong>{fmt(t.creditBill)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Other Sale</span>
          <strong>{fmt(t.otherSale)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Sale Collection</span>
          <strong>{fmt(c.saleCollection)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Subtotal</span>
          <strong>{fmt(subtotal)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Less Cash Discount</span>
          <strong>{fmt(c.lessCashDiscount)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Less Sale Return</span>
          <strong>{fmt(c.lessSaleReturn ?? t.lessSR)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Total Cash In Hand</span>
          <strong>{fmt(totalCashInHand)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Total Sale</span>
          <strong>{fmt(f.totalSale)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Total Purchase</span>
          <strong>{fmt(f.totalPurchase)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Transfer Out</span>
          <strong>{fmt(f.branchTransferOut ?? f.transferOutCurr)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Transfer In</span>
          <strong>{fmt(f.branchTransferIn)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Misc Sale</span>
          <strong>{fmt(f.miscSale)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Misc Purchase</span>
          <strong>{fmt(f.miscPurchase)}</strong>
        </div>
        <div className="msr-precalc-total-box msr-bp-box-oe">
          <span>OE Sale ({fmtPct(ss.oePct)})</span>
          <strong>{fmt(ss.oeAmt)}</strong>
        </div>
        <div className="msr-precalc-total-box msr-bp-box-ord">
          <span>Ordinary Sale ({fmtPct(ss.ordinaryPct)})</span>
          <strong>{fmt(ss.ordinaryAmt)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Avg Day Sale (curr)</span>
          <strong>{fmt(avg.curr)}</strong>
        </div>
        <div className="msr-precalc-total-box">
          <span>Working Days</span>
          <strong>
            {wd.curr ?? "—"} / {wd.prev ?? "—"}
          </strong>
        </div>
      </div>
    </section>
  );
}

function ReportBrandsTable({ report }) {
  const brands = report?.brands || [];
  if (!brands.length) return null;

  const sr = report.saleReturnByBrand || {};
  const visible = brands.filter((b) => {
    const name = b.name || "";
    if (name === "MAHINDRA") return (Number(b.currSale) || 0) > 0.01;
    return true;
  });

  const totals = visible.reduce(
    (acc, b) => ({
      prevSale: acc.prevSale + (Number(b.prevSale) || 0),
      currSale: acc.currSale + (Number(b.currSale) || 0),
      purchasePrev: acc.purchasePrev + (Number(b.purchasePrev) || 0),
      purchaseCurr: acc.purchaseCurr + (Number(b.purchaseCurr) || 0),
      oeAmt: acc.oeAmt + (Number(b.oeAmt) || 0),
      otherAmt: acc.otherAmt + (Number(b.otherAmt) || 0),
      saleReturn: acc.saleReturn + (Number(sr[b.name]) || 0),
    }),
    { prevSale: 0, currSale: 0, purchasePrev: 0, purchaseCurr: 0, oeAmt: 0, otherAmt: 0, saleReturn: 0 }
  );

  return (
    <section className="msr-precalc-panel">
      <h2 className="msr-precalc-title">Import report — brand-wise sales</h2>
      <p className="msr-precalc-meta">
        Current / previous month sale &amp; purchase · OE &amp; ordinary split · sale return by brand
      </p>
      <div className="msr-precalc-table-wrap">
        <table className="msr-precalc-table">
          <thead>
            <tr>
              <th>Brand</th>
              <th className="msr-num">Top Sale</th>
              <th className="msr-num">Prev Sale</th>
              <th className="msr-num">Curr Sale</th>
              <th className="msr-num">Change</th>
              <th className="msr-num">Prev Purchase</th>
              <th className="msr-num">Curr Purchase</th>
              <th className="msr-num">OE %</th>
              <th className="msr-num">OE Amt</th>
              <th className="msr-num">Ord %</th>
              <th className="msr-num">Ord Amt</th>
              <th className="msr-num">Sale Return</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((b) => (
              <tr key={b.name}>
                <td>{b.name}</td>
                <td className="msr-num">{fmt(b.topSale)}</td>
                <td className="msr-num">{fmt(b.prevSale)}</td>
                <td className="msr-num">{fmt(b.currSale)}</td>
                <td className="msr-num">{fmtPct(b.salePct)}</td>
                <td className="msr-num">{fmt(b.purchasePrev)}</td>
                <td className="msr-num">{fmt(b.purchaseCurr)}</td>
                <td className="msr-num">{fmtPct(b.oePct)}</td>
                <td className="msr-num">{fmt(b.oeAmt)}</td>
                <td className="msr-num">{fmtPct(b.otherPct)}</td>
                <td className="msr-num">{fmt(b.otherAmt)}</td>
                <td className="msr-num">{fmt(sr[b.name])}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="font-semibold">Total</td>
              <td className="msr-num">—</td>
              <td className="msr-num font-semibold">{fmt(totals.prevSale)}</td>
              <td className="msr-num font-semibold">{fmt(totals.currSale)}</td>
              <td className="msr-num">—</td>
              <td className="msr-num font-semibold">{fmt(totals.purchasePrev)}</td>
              <td className="msr-num font-semibold">{fmt(totals.purchaseCurr)}</td>
              <td className="msr-num">—</td>
              <td className="msr-num font-semibold">{fmt(totals.oeAmt)}</td>
              <td className="msr-num">—</td>
              <td className="msr-num font-semibold">{fmt(totals.otherAmt)}</td>
              <td className="msr-num font-semibold">{fmt(totals.saleReturn)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function ReconciliationPanel({ totals }) {
  if (!totals) return null;
  const rows = [
    { label: "Report footer — total sale", value: totals.totalSale },
    { label: "Report footer — total purchase", value: totals.totalPurchase },
    { label: "Brand lines — sale sum", value: totals.brandLineSale },
    { label: "Brand lines — purchase sum", value: totals.brandLinePurchase },
    { label: "Brand lines — sale return sum", value: totals.brandLineSaleReturn },
    { label: "Group lines — sale sum", value: totals.groupLineSale },
    { label: "Group lines — purchase sum", value: totals.groupLinePurchase },
  ].filter((r) => r.value != null && r.value !== 0);

  if (!rows.length) return null;

  return (
    <section className="msr-precalc-panel">
      <h2 className="msr-precalc-title">Line aggregation check</h2>
      <p className="msr-precalc-meta">
        Compares report footer totals with summed brand / group import lines used for Employee Portal &
        incentives.
      </p>
      <div className="msr-precalc-table-wrap">
        <table className="msr-precalc-table">
          <thead>
            <tr>
              <th>Measure</th>
              <th className="msr-num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td>{r.label}</td>
                <td className="msr-num">{fmt(r.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function MonthlySaleReportImportPrecalcPage() {
  const now = new Date();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = readMonthlySaleReportParams(searchParams, now);

  const [fyStartYear, setFyStartYear] = useState(initial.fyStartYear);
  const [fySlot, setFySlot] = useState(initial.fySlot);
  const [branchFilter, setBranchFilter] = useState(initial.branchFilter);

  const [saved, setSaved] = useState(null);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState("");
  const [topSaleValues, setTopSaleValues] = useState(() => emptyTopSalesValues());
  const [topSaleResolved, setTopSaleResolved] = useState(null);
  const [savingTopSale, setSavingTopSale] = useState(false);
  const [topSaleOpen, setTopSaleOpen] = useState(false);
  const [saleCollectionOpen, setSaleCollectionOpen] = useState(false);
  const [saleCollectionValue, setSaleCollectionValue] = useState("");
  const [saleCollectionSaved, setSaleCollectionSaved] = useState(false);
  const [savingSaleCollection, setSavingSaleCollection] = useState(false);

  const { calendarYear: year, monthIndex } = getCalendarMonthFromFySlot(fyStartYear, fySlot);
  const fyLabel = formatFinancialYearLabel(fyStartYear);
  const fySelectOptions = useMemo(() => getFinancialYearDropdownOptions(fyStartYear), [fyStartYear]);
  const fyMonthSequence = useMemo(() => getFinancialYearMonthSequence(fyStartYear), [fyStartYear]);

  const reportHref = `/reports/monthly-sale-report-import?${monthlySaleReportQuery(
    fyStartYear,
    fySlot,
    branchFilter
  )}`;

  useEffect(() => {
    setSearchParams(monthlySaleReportQuery(fyStartYear, fySlot, branchFilter), { replace: true });
  }, [fyStartYear, fySlot, branchFilter, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoadingSaved(true);
    setTopSaleValues(emptyTopSalesValues());
    setTopSaleResolved(null);
    setTopSaleOpen(false);
    setSaleCollectionOpen(false);
    setSaleCollectionValue("");
    setSaleCollectionSaved(false);
    (async () => {
      try {
        const [data, resolved, collectionDoc] = await Promise.all([
          loadMonthlyImportPrecalc({ year, monthIndex, branchFilter }),
          resolveMonthlyTopSales(year, monthIndex, branchFilter),
          loadMonthlySaleCollection(year, monthIndex, branchFilter),
        ]);
        if (cancelled) return;
        const values = { ...emptyTopSalesValues() };
        if (resolved?.found) {
          for (const brand of MONTHLY_AUTOS_BRANDS) {
            const n = resolved.values?.[brand];
            values[brand] = n != null && n !== "" ? String(n) : "";
          }
        }
        if (data?.report) {
          applyTopSalesToReport(data.report, resolved?.found ? resolved.values : {});
          if (collectionDoc?.amount != null) {
            applySaleCollectionToReport(data.report, collectionDoc.amount);
          }
        }
        setTopSaleValues(values);
        setTopSaleResolved(resolved);
        setSaleCollectionSaved(Boolean(collectionDoc));
        setSaleCollectionValue(
          collectionDoc?.amount != null
            ? String(collectionDoc.amount)
            : data?.report?.collection?.saleCollection != null
              ? String(data.report.collection.saleCollection)
              : ""
        );
        setSaved(data);
      } catch (e) {
        if (!cancelled) {
          setSaved(null);
          toast.error(e?.message || String(e));
        }
      } finally {
        if (!cancelled) setLoadingSaved(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, monthIndex, branchFilter]);

  const handleTopSaleChange = (brand, value) => {
    setTopSaleValues((prev) => ({ ...prev, [brand]: value }));
  };

  const handleSaveTopSale = async () => {
    setSavingTopSale(true);
    try {
      let userEmail = "";
      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        userEmail = u?.email || "";
      } catch {
        /* ignore */
      }
      await saveMonthlyTopSales({
        year,
        monthIndex,
        branchFilter,
        values: topSaleValues,
        updatedBy: userEmail,
      });
      clearImportMonthlyReportCache();
      const resolved = await resolveMonthlyTopSales(year, monthIndex, branchFilter);
      setTopSaleResolved(resolved);
      setSaved((prev) => {
        if (!prev?.report) return prev;
        applyTopSalesToReport(prev.report, resolved?.found ? resolved.values : {});
        return { ...prev };
      });
      toast.success("Top Sale saved. Import report will use these until you save a later month.");
      setTopSaleOpen(false);
    } catch (e) {
      toast.error(e?.message || String(e));
    } finally {
      setSavingTopSale(false);
    }
  };

  const getUserEmail = () => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u?.email || "";
    } catch {
      return "";
    }
  };

  const handleSaveSaleCollection = async () => {
    setSavingSaleCollection(true);
    try {
      await saveMonthlySaleCollection({
        year,
        monthIndex,
        branchFilter,
        amount: saleCollectionValue,
        updatedBy: getUserEmail(),
      });
      clearImportMonthlyReportCache();
      clearPrecalcMemoryCache();
      const savedDoc = await loadMonthlySaleCollection(year, monthIndex, branchFilter);
      setSaleCollectionSaved(Boolean(savedDoc));
      if (savedDoc?.amount != null) setSaleCollectionValue(String(savedDoc.amount));
      setSaved((prev) => {
        if (!prev?.report) return prev;
        applySaleCollectionToReport(prev.report, savedDoc?.amount);
        return { ...prev };
      });
      toast.success("Sale Collection saved for this month.");
      setSaleCollectionOpen(false);
    } catch (e) {
      toast.error(e?.message || String(e));
    } finally {
      setSavingSaleCollection(false);
    }
  };

  const handleClearSaleCollection = async () => {
    setSavingSaleCollection(true);
    try {
      await clearMonthlySaleCollection(year, monthIndex, branchFilter);
      clearImportMonthlyReportCache();
      clearPrecalcMemoryCache();
      const data = await loadMonthlyImportPrecalc({ year, monthIndex, branchFilter });
      if (data?.report) {
        applyTopSalesToReport(data.report, topSaleResolved?.found ? topSaleResolved.values : {});
      }
      setSaved(data);
      setSaleCollectionSaved(false);
      setSaleCollectionValue(
        data?.report?.collection?.saleCollection != null
          ? String(data.report.collection.saleCollection)
          : ""
      );
      toast.success("Manual Sale Collection cleared. Import total is used again.");
      setSaleCollectionOpen(false);
    } catch (e) {
      toast.error(e?.message || String(e));
    } finally {
      setSavingSaleCollection(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setProgress("Starting…");
    try {
      let userEmail = "";
      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        userEmail = u?.email || "";
      } catch {
        /* ignore */
      }

      const result = await generateAndSaveMonthlyImportPrecalc(
        { year, monthIndex, branchFilter, generatedBy: userEmail },
        setProgress
      );
      setSaved(result);
      toast.success("Monthly import totals saved to Firestore.");
    } catch (e) {
      toast.error(e?.message || String(e));
    } finally {
      setGenerating(false);
      setProgress("");
    }
  };

  const handlePrintGroupLines = () => {
    const cleanup = () => {
      document.body.classList.remove("msr-printing-group-lines");
      window.removeEventListener("afterprint", cleanup);
    };
    document.body.classList.add("msr-printing-group-lines");
    window.addEventListener("afterprint", cleanup);
    window.print();
    window.setTimeout(cleanup, 1500);
  };

  const totals = saved?.totals;
  const saleReturnBrandRows = useMemo(() => {
    if (saved?.byBrandSaleReturn?.length) return saved.byBrandSaleReturn;
    return buildByBrandSaleReturn(saved?.report);
  }, [saved]);

  const generatedAtLabel = formatGeneratedAt(saved?.generatedAt);

  return (
    <>
      <div className="msr-no-print">
        <MenuBar />
        <HeaderBar />
      </div>

      <main className="msr-upload-page msr-upload-page--full">
        <header className="msr-upload-page-head msr-no-print">
          <div>
            <p className="msr-upload-page-kicker">Reports</p>
            <h1 className="msr-upload-page-title">Monthly import — generate totals</h1>
            <p className="msr-upload-page-sub">
              Select <strong>month</strong>, then <strong>branch</strong>. Generate sale, purchase,
              transfer out/in, and <strong>sale return brand-wise</strong> totals. Use{" "}
              <strong>Edit Top Sale</strong> to enter brand Top Sale (carries forward until you save a
              later month). Use <strong>Edit Sale Collection</strong> for this month’s SALE COLLECTION
              line.
            </p>
          </div>
          <Link to={reportHref} className="msr-upload-page-back">
            Open import report →
          </Link>
        </header>

        <div className="msr-toolbar msr-upload-toolbar msr-no-print">
          <label className="msr-toolbar-label">
            Financial year{" "}
            <select
              className="msr-toolbar-select"
              value={fyStartYear}
              onChange={(e) => setFyStartYear(Number(e.target.value))}
            >
              {fySelectOptions.map(({ startYear, label }) => (
                <option key={startYear} value={startYear}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="msr-toolbar-label">
            Month{" "}
            <select
              className="msr-toolbar-select"
              value={fySlot}
              onChange={(e) => setFySlot(Number(e.target.value))}
            >
              {fyMonthSequence.map((s) => (
                <option key={`${s.calendarYear}-${s.monthIndex}`} value={s.fySlot}>
                  {MONTH_OPTIONS[s.monthIndex]} {s.calendarYear}
                </option>
              ))}
            </select>
          </label>
          <span className="msr-toolbar-fy-hint">{fyLabel} · April–March</span>
          <label className="msr-toolbar-label">
            Branch{" "}
            <select
              className="msr-toolbar-select"
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
            >
              {BRANCH_OPTIONS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="msr-print-btn"
            disabled={generating}
            onClick={handleGenerate}
          >
            {generating ? progress || "Generating…" : "Generate & upload"}
          </button>
          <button
            type="button"
            className="msr-print-btn msr-top-sale-btn"
            disabled={generating || savingTopSale || savingSaleCollection}
            onClick={() => setTopSaleOpen(true)}
          >
            Edit Top Sale
          </button>
          <button
            type="button"
            className="msr-print-btn msr-top-sale-btn"
            disabled={generating || savingTopSale || savingSaleCollection}
            onClick={() => setSaleCollectionOpen(true)}
          >
            Edit Sale Collection
          </button>
        </div>

        {generating ? (
          <p className="msr-toolbar-status">{progress || "Generating monthly import totals…"}</p>
        ) : null}
        {loadingSaved ? <p className="msr-toolbar-status">Checking saved totals…</p> : null}

        <TopSaleEditorPopup
          open={topSaleOpen}
          onClose={() => setTopSaleOpen(false)}
          year={year}
          monthIndex={monthIndex}
          branchLabel={BRANCH_OPTIONS.find((b) => b.value === branchFilter)?.label || branchFilter}
          resolved={topSaleResolved}
          values={topSaleValues}
          onChange={handleTopSaleChange}
          onSave={handleSaveTopSale}
          saving={savingTopSale}
        />

        <SaleCollectionEditorPopup
          open={saleCollectionOpen}
          onClose={() => setSaleCollectionOpen(false)}
          year={year}
          monthIndex={monthIndex}
          branchLabel={BRANCH_OPTIONS.find((b) => b.value === branchFilter)?.label || branchFilter}
          value={saleCollectionValue}
          onChange={setSaleCollectionValue}
          onSave={handleSaveSaleCollection}
          onClear={handleClearSaleCollection}
          saving={savingSaleCollection}
          hasSaved={saleCollectionSaved}
        />

        {saved ? (
          <>
            <section className="msr-precalc-panel">
              <h2 className="msr-precalc-title">
                Saved totals — {MONTH_OPTIONS[monthIndex]} {year} ·{" "}
                {BRANCH_OPTIONS.find((b) => b.value === branchFilter)?.label || branchFilter}
              </h2>
              {generatedAtLabel ? (
                <p className="msr-precalc-meta">
                  Last generated: {generatedAtLabel}
                  {saved.generatedBy ? ` · ${saved.generatedBy}` : ""}
                  {saved.lineCount != null
                    ? ` · ${Number(saved.lineCount).toLocaleString()} import lines`
                    : ""}
                  {saved.docId || saved.id ? (
                    <>
                      {" · "}
                      Firestore doc{" "}
                      <code className="msr-precalc-doc-id">{saved.docId || saved.id}</code>
                      {" "}
                      ({MONTH_OPTIONS[monthIndex]} {year} ·{" "}
                      {BRANCH_OPTIONS.find((b) => b.value === branchFilter)?.label || branchFilter})
                    </>
                  ) : null}
                </p>
              ) : null}
              <div className="msr-precalc-totals-grid">
                <div className="msr-precalc-total-box">
                  <span>Cash Sale</span>
                  <strong>{fmt(totals?.cashSale)}</strong>
                </div>
                <div className="msr-precalc-total-box">
                  <span>Credit Bill</span>
                  <strong>{fmt(totals?.creditBill)}</strong>
                </div>
                <div className="msr-precalc-total-box">
                  <span>Other Sale</span>
                  <strong>{fmt(totals?.otherSale)}</strong>
                </div>
                <div className="msr-precalc-total-box msr-bp-box-total">
                  <span>Total Sale</span>
                  <strong>{fmt(totals?.totalSale)}</strong>
                </div>
                <div className="msr-precalc-total-box">
                  <span>Total Purchase</span>
                  <strong>{fmt(totals?.totalPurchase)}</strong>
                </div>
                <div className="msr-precalc-total-box">
                  <span>Transfer Out</span>
                  <strong>{fmt(totals?.transferOut)}</strong>
                </div>
                <div className="msr-precalc-total-box">
                  <span>Transfer In</span>
                  <strong>{fmt(totals?.transferIn)}</strong>
                </div>
                <div className="msr-precalc-total-box">
                  <span>Sale Return</span>
                  <strong>{fmt(totals?.lessSaleReturn)}</strong>
                </div>
              </div>
            </section>

            <ReportCollectionPanel report={saved.report} />
            <ReportBrandsTable report={saved.report} />
            <ReconciliationPanel totals={totals} />

            <SaleReturnBrandTable rows={saleReturnBrandRows} total={totals?.lessSaleReturn} />
            <BreakdownTable
              title="Brand-wise lines (BrandName on import)"
              meta="Aggregated from import line Amount · used for OE/Ord incentive & party deductions"
              rows={saved.byBrand}
            />
            <BreakdownTable
              title="Group-wise lines (Item Group on import)"
              meta="Aggregated from import line Amount · used for Employee Portal special incentive & group reports"
              rows={saved.byGroup}
              printClassName="msr-group-lines-print"
              printLabel={`${MONTH_OPTIONS[monthIndex]} ${year} · ${
                BRANCH_OPTIONS.find((b) => b.value === branchFilter)?.label || branchFilter
              }`}
              onPrint={handlePrintGroupLines}
            />
          </>
        ) : !loadingSaved ? (
          <p className="msr-empty-msg">
            No saved totals for {MONTH_OPTIONS[monthIndex]} {year}. Click{" "}
            <strong>Generate & upload</strong> once — the import report will then load instantly and full
            details will appear here.
          </p>
        ) : null}
      </main>
    </>
  );
}
